/* global femcare */

/** Provides behavior for:
 * - Switching product colors
 * - Loading product images dynamically via AJAX
 * - Updating prices and handling image fallbacks
 * */
femcare.ctaPanel.colorSelector = {

    panels: document.querySelectorAll('.cta-panel'),
    antiforgeryToken: null,

    /**
     * Initializes the color selector.
     * Retrieves anti-forgery token and binds click events if panels exist.
     */
    init: function () {
        if (this.panels.length) {
            this.antiforgeryToken = femcare.product.cart.getAntiforgeryToken();
            this.bindEvents();
        }
    },

    /**
     * Binds color selection events to all .product-color buttons within each CTA panel.
     * Handles AJAX request to fetch updated product data based on selected color.
     */
    bindEvents: function () {
        this.panels.forEach(panel => {
            const colorButtons = panel.querySelectorAll('.product-color');
            const ctaImage = panel.querySelector('.cta-panel__image');
            const img = ctaImage ? ctaImage.querySelector('img') : null;
            const sources = ctaImage ? ctaImage.querySelectorAll('source') : [];
            const hoverImage = panel.querySelector('.cta-panel__quick-buy img');
            const url = panel.dataset.productUrl;

            const initialColorButton = panel.querySelector('.product-color[data-selected]');

            colorButtons.forEach(btn => {
                btn.addEventListener('click', () => {
                    this.handleColorButtonClick(btn, colorButtons, panel, ctaImage, img, sources, hoverImage, initialColorButton);
                });
            });

            if (url) {
                this.bindPanelNavigation(panel, url);
            }
        });
    },

    /**
     * Binds (or re-binds) panel-level click and keyboard navigation to the given URL.
     * Uses AbortController to remove any previously registered listeners before adding new ones,
     * preventing duplicate handlers when called again after a color change.
     *
     * @param {HTMLElement} panel - The CTA panel element.
     * @param {string} navigationUrl - The product URL to navigate to when the panel is activated.
     */
    bindPanelNavigation: function (panel, navigationUrl) {
        if (panel._navigationAbortController) {
            panel._navigationAbortController.abort();
        }

        const abortController = new AbortController();
        panel._navigationAbortController = abortController;
        const { signal } = abortController;

        panel.addEventListener('click', e => {
            if (!e.target.closest('button') && !e.target.closest('a')) {
                window.location.href = navigationUrl;
            }
        }, { signal });

        panel.addEventListener('keydown', e => {
            if (e.key === 'Enter' && e.target === panel) {
                window.location.href = navigationUrl;
            }
        }, { signal });
    },

    /**
     * Reads card variations and applies the currently selected color.
     * @param {HTMLElement} productCard - Product card.
     * @returns {Array} Selected variation names and values.
     */
    getVariationAttributes: function (productCard) {
        const variationAttributes = [];
        Object.entries(productCard.dataset).forEach(([attributeName, attributeValue]) => {
            if (!/^variation\d+$/.test(attributeName)) {
                return;
            }
            try {
                const variation = JSON.parse(attributeValue);
                const name = variation?.name ?? variation?.Name;
                const value = variation?.value ?? variation?.Value;
                if (name && value !== undefined && value !== null) {
                    variationAttributes.push({ name, value });
                }
            } catch {
                console.error('Invalid product variation:', attributeName);
            }
        });

        const selectedColor = productCard.querySelector('.product-color[data-selected]');
        if (selectedColor?.dataset.name && selectedColor.dataset.value) {
            const colorVariation = variationAttributes.find(variation => variation.name === selectedColor.dataset.name);
            if (colorVariation) {
                colorVariation.value = selectedColor.dataset.value;
            } else {
                variationAttributes.push({ name: selectedColor.dataset.name, value: selectedColor.dataset.value });
            }
        }
        return variationAttributes;
    },

    /**
     * Handles the color button click event.
     * Updates the selected state and fetches new product data via AJAX.
     *
     * @param {HTMLElement} btn - The clicked color button.
     * @param {NodeListOf<HTMLElement>} colorButtons - All color buttons in the panel.
     * @param {HTMLElement} panel - The CTA panel element.
     * @param {HTMLElement} ctaImage - The CTA image container.
     * @param {HTMLImageElement} img - Main product image element.
     * @param {NodeListOf<HTMLSourceElement>} sources - All source elements for responsive images.
     * @param {HTMLImageElement} hoverImage - Optional hover image element.
     * @param {HTMLElement} initialColorButton - The color button selected by default on page load.
     */
    handleColorButtonClick: function (btn, colorButtons, panel, ctaImage, img, sources, hoverImage, initialColorButton) {
        colorButtons.forEach(b => b.removeAttribute('data-selected'));
        btn.setAttribute('data-selected', 'true');

        const data = {
            ean: panel.dataset.ean,
            url: panel.dataset.productUrl,
            variationAttributes: this.getVariationAttributes(panel)
        };
        femcare.loader.toggleLoader(ctaImage, true);
        const oldSrc = img.src;
        const oldHoverSrc = hoverImage?.src || "";
        img.src = "";
        if (hoverImage) {
            hoverImage.src = "";
        }
        $.ajax({
            url: femcare.shared.util.baseUrl + 'ajax/products/product/detailproductsku',
            method: 'POST',
            data: JSON.stringify(data),
            contentType: 'application/json',
            headers: { 'X-Request-Verification-Token': this.antiforgeryToken },
            success: (res) => {
                const contentData = typeof res.content === 'string' ? JSON.parse(res.content) : res.content;
                const product = contentData.product;

                if (!product) {
                    img.src = oldSrc;
                    if (hoverImage) {
                        hoverImage.src = oldHoverSrc;
                    }
                    femcare.loader.toggleLoader(ctaImage, false);
                    return;
                }

                this.updateProductImages(product, img, sources, hoverImage, oldSrc, oldHoverSrc, () => {
                    femcare.loader.toggleLoader(ctaImage, false);
                });

                const priceInformation = product?.priceInformation;
                if (priceInformation) {
                    this.updateProductPrice(priceInformation, panel);
                }

                const selectedSkuId = product.productSkuId;
                const isInitialColorReselected = initialColorButton && btn.dataset.value === initialColorButton.dataset.value;

                const updatedNavigationUrl = new URL(panel.dataset.productUrl, window.location.origin);
                if (!isInitialColorReselected && selectedSkuId) {
                    updatedNavigationUrl.searchParams.set('variant_id', selectedSkuId);
                } else {
                    updatedNavigationUrl.searchParams.delete('variant_id');
                }

                panel.querySelectorAll('a').forEach(productCardLink => {
                    const productPageUrl = new URL(productCardLink.href, window.location.origin);
                    if (!isInitialColorReselected && selectedSkuId) {
                        productPageUrl.searchParams.set('variant_id', selectedSkuId);
                    } else {
                        productPageUrl.searchParams.delete('variant_id');
                    }
                    productCardLink.href = productPageUrl.toString();
                });

                this.bindPanelNavigation(panel, updatedNavigationUrl.toString());
            },
            error: () => {
                img.src = oldSrc;
                if (hoverImage) {
                    hoverImage.src = oldHoverSrc;
                }
                femcare.loader.toggleLoader(ctaImage, false);
            }
        })
    },

    /**
     * Updates product images after AJAX response.
     * Loads and replaces both primary and hover images.
     *
     * @param {Object} product - Product object returned from AJAX response.
     * @param {HTMLImageElement} img - Main product image element.
     * @param {NodeListOf<HTMLSourceElement>} sources - All <source> elements for responsive images.
     * @param {HTMLImageElement} hoverImage - Optional hover image element.
     * @param {string} oldSrc - Fallback source of the original image.
     * @param {string} oldHoverSrc - Fallback source of the original hover image.
     * @param {Function} [callback] - Called once all image updates are complete.
     */
    updateProductImages: function (product, img, sources, hoverImage, oldSrc, oldHoverSrc, callback) {
        if (!product?.images?.length) {
            this.setFallbackImages(img, hoverImage, oldSrc, oldHoverSrc, callback);
            return;
        }

        const newSrc = product.images[0]?.url;
        const hoverSrc = product.images[1]?.url;

        if (!newSrc) {
            this.setFallbackImages(img, hoverImage, oldSrc, oldHoverSrc, callback);
            return;
        }

        this.loadImage(
            newSrc,
            () => {
                img.src = newSrc;
                sources?.forEach(source => source.srcset = newSrc);

                if (hoverImage) {
                    if (hoverSrc) {
                        this.loadImage(
                            hoverSrc,
                            () => { hoverImage.src = hoverSrc; callback?.(); },
                            () => { hoverImage.src = oldHoverSrc; callback?.(); }
                        );
                    } else {
                        hoverImage.src = oldHoverSrc;
                        callback?.();
                    }
                } else {
                    callback?.();
                }
            },
            () => this.setFallbackImages(img, hoverImage, oldSrc, oldHoverSrc, callback)
        );
    },

    /**
     * Updates the product price displayed within the CTA panel.
     *
     * @param {Object} priceInformation - Object containing pricing details.
     * @param {HTMLElement} ctaPanel - The CTA panel where price will be updated.
     */
    updateProductPrice: function (priceInformation, ctaPanel) {
        const container = ctaPanel.querySelector(".cta-panel__price");
        let priceHtml = "";
        if (container) {
            if (priceInformation.showDiscount) {
                const ammountOldHtml = `<div class="cta-panel__amount cta-panel__amount--old">
                        ${priceInformation.priceWithoutDiscount}
                        </div>`;
                priceHtml += ammountOldHtml;
            }
            const amountCurrentHtml = `<div class="cta-panel__amount cta-panel__amount--current">
                        ${priceInformation.sellingPrice}
                        </div>`;
            priceHtml += amountCurrentHtml;
            container.innerHTML = priceHtml;
        }
    },

    /**
     * Restores previous images if loading fails.
     *
     * @param {HTMLImageElement} img - Main image element.
     * @param {HTMLImageElement} hoverImage - Optional hover image.
     * @param {string} oldSrc - Fallback main image source.
     * @param {string} oldHoverSrc - Fallback hover image source.
     * @param {Function} [callback] - Called after fallback applied.
     */
    setFallbackImages: function (img, hoverImage, oldSrc, oldHoverSrc, callback) {
        img.src = oldSrc;
        if (hoverImage) {
            hoverImage.src = oldHoverSrc;
        }
        callback?.();
    },

    /**
     * Loads an image.
     *
     * @param {string} src - URL of the image to load.
     * @param {Function} onSuccess - Callback when image loads successfully.
     * @param {Function} onError - Callback when image fails to load.
     */
    loadImage: function (src, onSuccess, onError) {
        const temp = new Image();
        temp.onload = onSuccess;
        temp.onerror = onError;
        temp.src = src;
    },
}